/**
 * Single source of truth for Statlign's AdSense units and placement rules.
 *
 * Both the React `AdSlot` component and the static prerenderer read this module,
 * so the ad markup a reviewer sees in the prerendered HTML is exactly the markup
 * the hydrated app renders. Drift between the two was one of the things that made
 * the previous AdSense submission impossible to review.
 */

export const ADSENSE_PUBLISHER_ID = "ca-pub-9761423989942935";

/** Live ad units created in the AdSense account. */
export const AD_UNITS = {
  /**
   * Above-the-content leaderboard. It re-uses the responsive in-article unit
   * (AdSense permits one unit in several positions) so there is no unconfigured
   * slot id anywhere in the markup.
   */
  header: "9662178650",
  /** In-article unit, placed after the first substantive content block. */
  inContent: "9662178650",
  /** Secondary unit, placed low on the page (sidebar/end-of-content). */
  sidebar: "2471888697",
  /**
   * Five additional positions, spread down the page so two ad spaces are never
   * adjacent. AdSense permits one unit in several positions, so these reuse the
   * approved unit ids rather than introducing unconfigured slots.
   */
  afterIntro: "9662178650",
  midEarly: "9662178650",
  mid: "2471888697",
  midLate: "9662178650",
  footer: "2471888697",
} as const;

export type AdUnitKey = keyof typeof AD_UNITS;

/** Reserved heights (px) — prevents any layout shift when a unit fills. */
export const AD_UNIT_MIN_HEIGHT: Record<AdUnitKey, number> = {
  header: 120,
  inContent: 280,
  sidebar: 250,
  afterIntro: 250,
  midEarly: 250,
  mid: 280,
  midLate: 250,
  footer: 250,
};

export const AD_LABEL = "Advertisement";

/**
 * Routes that must never carry ad markup: account, payment, admin and
 * transactional surfaces, plus anything behind authentication. AdSense policy
 * forbids ads next to sign-in forms and payment flows, and these pages carry no
 * publisher content worth monetising.
 */
/**
 * Restored for AdSense compliance: no ads on sign-in, payment, account, admin
 * or bookmaker-brand pages.
 *
 * Note: Google AdSense policy discourages ads beside sign-in and payment forms,
 * so restoring the old exclusions is the safer configuration. To do that, put
 * the prefixes back: "/auth", "/reset-password", "/wallet", "/premium",
 * "/settings", "/admin", "/member", "/dashboard", "/upload", "/apply", "/live",
 * "/docs/mcp", "/ai-access".
 */
export const AD_EXCLUDED_PREFIXES: string[] = [
  "/auth",
  "/reset-password",
  "/wallet",
  "/premium",
  "/ai-access",
  "/settings",
  "/upload",
  "/admin",
  "/match-curation",
  "/docs/mcp",
  "/member",
  "/dashboard",
  "/apply",
  // Bookmaker-brand pages: kept away from AdSense (gambling-promotion filters).
  "/predictions/bookmaker",
  "/vs",
];

/** True when a route is allowed to render ad units. */
export function adsAllowedOnRoute(path: string): boolean {
  // Strip a leading locale segment before matching. Statlign ships 2- and
  // 3-letter codes plus region variants (/es/…, /fil/…, /pt-BR/…, /zh-TW/…);
  // the old 2-letter-only pattern let ads render on /fil/premium and friends.
  const p = path.replace(/^\/[a-z]{2,3}(-[A-Za-z]{2,4})?(?=\/|$)/, "") || "/";
  const hit = (x: string) => AD_EXCLUDED_PREFIXES.some((prefix) => x === prefix || x.startsWith(`${prefix}/`));
  // Check the raw path too: real routes like /vs/… look like a locale segment.
  return !hit(path) && !hit(p);
}

/**
 * Static ad container markup for the prerendered document.
 *
 * Identical structure to `AdSlot`: labelled `<aside>`, reserved height, one
 * `<ins class="adsbygoogle">` per unit. The client hydrates these into live
 * units; a reviewer with JavaScript off still sees where the ads go.
 */
export function adSlotHtml(unit: AdUnitKey): string {
  const slot = AD_UNITS[unit];
  const minHeight = AD_UNIT_MIN_HEIGHT[unit];
  return (
    `<aside aria-label="${AD_LABEL}" data-statlign-ad-slot="${slot}" ` +
    `style="margin:2.5rem 0;width:100%;border-top:1px solid rgba(148,163,184,.35);padding-top:1.5rem">` +
    `<span style="display:block;margin-bottom:.5rem;font-size:10px;letter-spacing:.15em;` +
    `text-transform:uppercase;color:#94a3b8">${AD_LABEL}</span>` +
    `<ins class="adsbygoogle" style="display:block;min-height:${minHeight}px" ` +
    `data-ad-client="${ADSENSE_PUBLISHER_ID}" data-ad-slot="${slot}" data-ad-format="auto" ` +
    `data-full-width-responsive="true"></ins>` +
    `</aside>`
  );
}
